import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Title, Text, Paper, Button, Grid, Stack, Box, TextInput, Textarea, ActionIcon, Accordion, NumberInput, Select, Modal, Notification, Autocomplete, Badge, Group, Table } from "@mantine/core";
import { FormattedMessage, useIntl } from "react-intl";
import { FileSpreadsheet, Edit, Plus, Trash2, ArrowLeft } from "lucide-react";
import { IconMapPin, IconCheck, IconX } from "@tabler/icons-react";
import { InteractiveMap } from "../components/InteractiveMap";
import { BASE_URL } from "../Redux/apiConfig";
import { getProductsList, getUOMList } from "../Redux/products/slice";
import { setLanguage } from "../Redux/languageSlice"
import { SIGNUP_FEES } from "../configs/signupFees";

const SignUpFormPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const language = useSelector(state => state.language);
  
  // Get products and UOM from Redux
  const { productsList, UOMList } = useSelector((state) => state.products);
  
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // User Account Data (userId and password will be auto-generated)
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [preferredLanguageId, setPreferredLanguageId] = useState(null);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(true);
  
  // Profile Data - Basic Info
  const [profileTitle, setProfileTitle] = useState("");
  const [profileSubtitle, setProfileSubtitle] = useState("");
  const [description, setDescription] = useState("");
  
  // Profile Data - Contact Info
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactAddress, setContactAddress] = useState("");
  
  // Profile Data - Information Grid
  const [producer, setProducer] = useState("");
  const [locationInfo, setLocationInfo] = useState("");
  const [extension, setExtension] = useState("");
  const [mainCrop, setMainCrop] = useState("");
  const [experience, setExperience] = useState("");
  const [practices, setPractices] = useState("");
  const [certifications, setCertifications] = useState("");
  const [altitude, setAltitude] = useState("");
  
  // Profile Data - Social Media
  const [instagram, setInstagram] = useState("");
  const [twitter, setTwitter] = useState("");
  const [website, setWebsite] = useState("");
  
  // Location
  const [locationDescription, setLocationDescription] = useState("");
  
  // Map Selection State
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectingCoordIndex, setSelectingCoordIndex] = useState(null);
  const [mapView, setMapView] = useState({ center: [20, 0], zoom: 2 }); // Remember map position
  const [userLocation, setUserLocation] = useState(null);
  
  // Location autocomplete state
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  
  // Load products and UOM when language changes
  useEffect(() => {
    dispatch(getProductsList());
    dispatch(getUOMList());
  }, [dispatch, language]);

  const FALLBACK_LANGUAGES = [
    { value: '1', label: 'English' },
    { value: '2', label: 'French' },
    { value: '3', label: 'Spanish' },
    { value: '4', label: 'Portuguese' },
  ];

  // Maps app language code → LanguageName (matched against DB labels, case-insensitive)
  const APP_LANG_TO_NAME = { en: 'english', fr: 'french', es: 'spanish', pt: 'portuguese' };

  const applyDefaultLanguage = (options) => {
    const appLangName = APP_LANG_TO_NAME[language?.toLowerCase()];
    if (!appLangName) return;
    const match = options.find(o => o.label.toLowerCase() === appLangName);
    if (match) setPreferredLanguageId(match.value);
  };

  // Set language options directly from fallback (no backend call needed)
  useEffect(() => {
    setLanguageOptions(FALLBACK_LANGUAGES);
    applyDefaultLanguage(FALLBACK_LANGUAGES);
    setLanguagesLoading(false);
  }, []);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          setMapView({ center: [latitude, longitude], zoom: 13 });
        },
        (error) => {
          console.log('Geolocation not available:', error.message);
          // Keep default world view if geolocation fails
        }
      );
    }
  }, []);
  
  // Google API location search
  const searchLocations = async (query) => {
    if (!query) {
      setLocationSuggestions([]);
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/autocomplete?q=${encodeURIComponent(query)}&lang=${language}`);
      const data = await res.json();
      setLocationSuggestions(data.results || []);
    } catch (err) {
      console.error("Error Google API:", err);
    }
  };
  
  // Prepare sorted product list
  const sortedProductList = Array.from(productsList || [])
    .filter(item => typeof item.label === 'string')
    .sort((a, b) => a.label.localeCompare(b.label));
  
  // Calculate smallest product IDs to remove duplicates (same logic as CreatePropertiesModal)
  const calculateSmallestProductIds = () => {
    const smallestProductIds = {};
    sortedProductList.forEach(product => {
      if (!smallestProductIds[product.id_category] || product.id < smallestProductIds[product.id_category].id) {
        smallestProductIds[product.id_category] = { id: product.id_category, label: product.label };
      }
    });
    return smallestProductIds;
  };
  const smallestProductIds = calculateSmallestProductIds();
  
  // Prepare UOM options from Redux
  const unitOptions = (UOMList || []).map(uom => ({
    value: uom.UOM,
    label: uom.UOM
  }));
  
  // Farm Fields Data
  const [farmFields, setFarmFields] = useState([{
    name: "",
    product: "",
    region: "",
    coordinates: [{ lat: "", lng: "" }]
  }]);
  
  // Product Lots Data
  const [productLots, setProductLots] = useState([{
    tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Temporary ID for tracking
    ProductId: "", // Store id_category
    ProductInputValue: "", // For Autocomplete display
    Quantity: "",
    UOMId: "",
    UOMValue: "", // For Select display
    RegionId: "",
    RegionQuery: "", // For autocomplete display
    selectedLocation: null, // Store full location object with place_id
    periodStart: "",
    periodEnd: "",
    Period: "", // Combined period for backend
    ProductLotDetail: "",
    transportations: [],
    transformations: []
  }]);

  const addFarmField = () => {
    setFarmFields([...farmFields, {
      name: "",
      product: "",
      region: "",
      coordinates: [{ lat: "", lng: "" }]
    }]);
  };

  const removeFarmField = (index) => {
    setFarmFields(farmFields.filter((_, i) => i !== index));
  };

  const addCoordinate = (fieldIndex) => {
    const updated = [...farmFields];
    updated[fieldIndex].coordinates.push({ lat: "", lng: "" });
    setFarmFields(updated);
  };

  const removeCoordinate = (fieldIndex, coordIndex) => {
    const updated = [...farmFields];
    updated[fieldIndex].coordinates = updated[fieldIndex].coordinates.filter((_, i) => i !== coordIndex);
    setFarmFields(updated);
  };

  const addProductLot = () => {
    setProductLots([...productLots, {
      tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ProductId: "",
      ProductInputValue: "",
      Quantity: "",
      UOMId: "",
      UOMValue: "",
      RegionId: "",
      RegionQuery: "",
      selectedLocation: null,
      periodStart: "",
      periodEnd: "",
      Period: "",
      ProductLotDetail: "",
      transportations: [],
      transformations: []
    }]);
  };

  const removeProductLot = (index) => {
    setProductLots(productLots.filter((_, i) => i !== index));
  };

  const addTransportation = (lotIndex) => {
    const updated = [...productLots];
    const productLotTempId = updated[lotIndex].tempId;
    updated[lotIndex].transportations.push({
      productLotTempId: productLotTempId, // Reference to parent product lot
      location_id: "",
      locationQuery: "",
      selectedLocation: null,
      periodStart: "",
      periodEnd: "",
      transportDescription: ""
    });
    setProductLots(updated);
  };

  const removeTransportation = (lotIndex, transIndex) => {
    const updated = [...productLots];
    updated[lotIndex].transportations = updated[lotIndex].transportations.filter((_, i) => i !== transIndex);
    setProductLots(updated);
  };

  const addTransformation = (lotIndex) => {
    const updated = [...productLots];
    const productLotTempId = updated[lotIndex].tempId;
    updated[lotIndex].transformations.push({
      productLotTempId: productLotTempId, // Reference to parent product lot
      physicalState: "",
      location_id: "",
      locationQuery: "",
      selectedLocation: null,
      periodStart: "",
      periodEnd: "",
      transportDescription: ""
    });
    setProductLots(updated);
  };

  const removeTransformation = (lotIndex, transIndex) => {
    const updated = [...productLots];
    updated[lotIndex].transformations = updated[lotIndex].transformations.filter((_, i) => i !== transIndex);
    setProductLots(updated);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    
    // Format product lots data for API compatibility
    const formattedProductLots = productLots.map(lot => {
      // Combine period dates for product lot
      const periodValue = lot.periodStart && lot.periodEnd 
        ? `${lot.periodStart} - ${lot.periodEnd}` 
        : lot.Period || "";
      
      // Format transportations with combined period
      const formattedTransportations = lot.transportations.map(trans => ({
        productLotTempId: trans.productLotTempId, // Reference to parent lot
        location_id: trans.selectedLocation?.place_id || trans.location_id,
        period: trans.periodStart && trans.periodEnd 
          ? `${trans.periodStart} - ${trans.periodEnd}` 
          : "",
        transportDescription: trans.transportDescription || "",
        locationQuery: trans.locationQuery,
        // Keep periodStart/periodEnd for reference
        periodStart: trans.periodStart,
        periodEnd: trans.periodEnd
      }));
      
      // Format transformations with combined period
      const formattedTransformations = lot.transformations.map(trans => ({
        productLotTempId: trans.productLotTempId, // Reference to parent lot
        physicalState: trans.physicalState,
        location_id: trans.selectedLocation?.place_id || trans.location_id,
        period: trans.periodStart && trans.periodEnd 
          ? `${trans.periodStart} - ${trans.periodEnd}` 
          : "",
        transportDescription: trans.transportDescription || "",
        // Keep additional fields for reference
        locationQuery: trans.locationQuery,
        periodStart: trans.periodStart,
        periodEnd: trans.periodEnd
      }));
      
      return {
        // Temporary ID for tracking
        tempId: lot.tempId,
        
        // Required fields for /addLot API
        ProductId: lot.ProductId,
        Quantity: lot.Quantity ? Number(lot.Quantity) : 0,
        UOMId: lot.UOMId,
        RegionId: lot.selectedLocation?.place_id || lot.RegionId,
        Period: periodValue,
        ProductLotDetail: lot.ProductLotDetail || "",
        
        // Additional fields for UI reference
        ProductInputValue: lot.ProductInputValue,
        UOMValue: lot.UOMValue,
        RegionQuery: lot.RegionQuery,
        periodStart: lot.periodStart,
        periodEnd: lot.periodEnd,
        
        // Child records
        transportations: formattedTransportations,
        transformations: formattedTransformations
      };
    });
    
    // Transform profile data to match ProfilePresentation field names
    const formData = {
      user: { userName, email, languageId: preferredLanguageId ? Number(preferredLanguageId) : null },
      profile: { 
        // Basic info - use correct field names for ProfilePresentation
        title: profileTitle, 
        subtitle: profileSubtitle, 
        description,
        heroImage: '', // Can be added later
        headerColor: 'brown', // Default theme
        
        // Contact - nest in object for ProfilePresentation
        contact: {
          name: contactName,
          phone: contactPhone,
          email: email,
          address: contactAddress
        },
        
        // Info Grid - convert to array format for ProfilePresentation
        infoGrid: [
          producer && { label: 'Producer', value: producer },
          locationInfo && { label: 'Location', value: locationInfo },
          extension && { label: 'Extension', value: extension },
          mainCrop && { label: 'Main Crop', value: mainCrop },
          experience && { label: 'Experience', value: experience },
          practices && { label: 'Practices', value: practices },
          certifications && { label: 'Certifications', value: certifications },
          altitude && { label: 'Altitude', value: altitude },
        ].filter(Boolean), // Remove empty values
        
        // Social - rename to socialLinks for ProfilePresentation
        socialLinks: {
          instagram,
          twitter,
          website
        },
        
        // Location - nest in object for ProfilePresentation
        location: {
          description: locationDescription
        },
        
        // Initialize empty arrays for ProfilePresentation
        gallery: [],
        documents: []
      },
      farmFields,
      productLots: formattedProductLots,
      submittedAt: new Date().toISOString()
    };
    
    // Log formatted data for verification
    console.log('=== Formatted Signup Data ===');
    console.log('Product Lots with temporary IDs for tracking:');
    formattedProductLots.forEach((lot, index) => {
      console.log(`\nProduct Lot ${index + 1}:`);
      console.log(`  Temp ID: ${lot.tempId}`);
      console.log(`  ProductId: ${lot.ProductId}, Quantity: ${lot.Quantity}, UOMId: ${lot.UOMId}`);
      console.log(`  Transportations (${lot.transportations.length}):`);
      lot.transportations.forEach((trans, tIndex) => {
        console.log(`    [${tIndex + 1}] References Lot: ${trans.productLotTempId}`);
      });
      console.log(`  Transformations (${lot.transformations.length}):`);
      lot.transformations.forEach((trans, tIndex) => {
        console.log(`    [${tIndex + 1}] References Lot: ${trans.productLotTempId}`);
      });
    });
    console.log('\nFull Data:', JSON.stringify(formattedProductLots, null, 2));
    
    try {
      const response = await fetch(`${BASE_URL}/saveSignup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitSuccess(true);
        console.log('Form submitted successfully:', result.fileName);
      } else {
        setSubmitError(result.message || 'An error occurred while submitting the form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('An error occurred while submitting the form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!showForm) {
    return (
      <Box style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", paddingTop: "4rem", paddingBottom: "4rem" }}>
        <Container size="lg" px="md">
          <Stack spacing="xl" align="center">
            <Title order={1} align="center" style={{ fontSize: "2.5rem", color: "#2c3e50" }}>
              <FormattedMessage id="app.signupForm.title" />
            </Title>
            
            <Text size="lg" color="dimmed" align="center" style={{ maxWidth: "600px" }}>
              <FormattedMessage id="app.signupForm.subtitle" />
            </Text>

            {/* Language switcher */}
            <Group position="right" style={{ width: "100%" }}>
              <select
                value={language}
                onChange={(e) => {
                  dispatch(setLanguage(e.target.value));
                  localStorage.setItem("language", JSON.stringify(e.target.value));
                }}
                style={{ fontSize: "14px", padding: "4px 8px", borderRadius: "4px", border: "1px solid #ced4da" }}
              >
                <option value="en"><FormattedMessage id="languageOptions.en" /></option>
                <option value="fr"><FormattedMessage id="languageOptions.fr" /></option>
                <option value="es"><FormattedMessage id="languageOptions.es" /></option>
                <option value="pt"><FormattedMessage id="languageOptions.pt" /></option>
              </select>
            </Group>

            <Grid gutter="xl" style={{ width: "100%", marginTop: "2rem" }}>
              <Grid.Col xs={12} md={6} style={{ marginBottom: "1rem" }}>
                <Paper
                  shadow="md"
                  p="xl"
                  radius="md"
                  style={{
                    textAlign: "center",
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.12)";
                  }}
                  onClick={() => setShowForm(true)}
                >
                  <Stack spacing="md" align="center">
                    <Edit size={60} color="#1c7ed6" />
                    <Title order={3} style={{ color: "#2c3e50" }}>
                      <FormattedMessage id="app.signupForm.option.web.title" />
                    </Title>
                    <Text color="dimmed" style={{ marginBottom: "1rem" }}>
                      <FormattedMessage id="app.signupForm.option.web.description" />
                    </Text>
                    <Button 
                      color="blue" 
                      size="lg" 
                      leftIcon={<Edit size={20} />}
                      fullWidth
                    >
                      <FormattedMessage id="app.signupForm.option.web.button" />
                    </Button>
                  </Stack>
                </Paper>
              </Grid.Col>

              <Grid.Col xs={12} md={6} style={{ marginBottom: "1rem" }}>
                <Paper
                  shadow="md"
                  p="xl"
                  radius="md"
                  style={{
                    textAlign: "center",
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.12)";
                  }}
                >
                  <Stack spacing="md" align="center">
                    <FileSpreadsheet size={60} color="#2f9e44" />
                    <Title order={3} style={{ color: "#2c3e50" }}>
                      <FormattedMessage id="app.signupForm.option.excel.title" />
                    </Title>
                    <Text color="dimmed" style={{ marginBottom: "1rem" }}>
                      <FormattedMessage id="app.signupForm.option.excel.description" />
                    </Text>
                    <Button 
                      color="green" 
                      size="lg" 
                      leftIcon={<FileSpreadsheet size={20} />}
                      fullWidth
                      onClick={async () => {
                        try {
                          const response = await fetch(`${BASE_URL}/api/downloadSignupTemplate/${language}`);
                          const data = await response.json();
                          if (data.success) {
                            window.open(data.downloadUrl, '_blank');
                          } else {
                            console.error('Failed to get download URL:', data.error);
                            alert('Failed to download template. Please try again.');
                          }
                        } catch (error) {
                          console.error('Error downloading template:', error);
                          alert('Failed to download template. Please try again.');
                        }
                      }}
                    >
                      <FormattedMessage id="app.signupForm.option.excel.button" />
                    </Button>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Stack>
        </Container>
      </Box>
    );
  }

  // Validation logic
  const isFormValid = () => {
    // User account fields
    const accountFieldsFilled = userName && email && preferredLanguageId;
    
    // Required profile fields
    const requiredFieldsFilled = profileTitle && profileSubtitle && description && contactName && contactPhone && contactAddress;
    
    // Main crop is required
    const mainCropValid = mainCrop && mainCrop.trim();
    
    return accountFieldsFilled && requiredFieldsFilled && mainCropValid;
  };

  // Get missing fields for detailed error message
  const getMissingFields = () => {
    const missing = [];
    if (!userName) missing.push('Username');
    if (!email) missing.push('Email');
    if (!preferredLanguageId) missing.push('Preferred Language');
    if (!profileTitle) missing.push('Profile Title');
    if (!profileSubtitle) missing.push('Profile Subtitle');
    if (!description) missing.push('Description');
    if (!contactName) missing.push('Contact Name');
    if (!contactPhone) missing.push('Contact Phone');
    if (!contactAddress) missing.push('Contact Address');
    if (!mainCrop || !mainCrop.trim()) missing.push('Main Crop');
    
    return missing;
  };

  return (
    <Box style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", padding: "2rem 0" }}>
      <Container size="lg" px="md">
        <Stack spacing="xl">
          <Box>
            <Button
              variant="subtle"
              color="gray"
              leftIcon={<ArrowLeft size={20} />}
              onClick={() => setShowForm(false)}
              mb="md"
            >
              <FormattedMessage id="app.signupForm.backButton" defaultMessage="Back to options" />
            </Button>
            <Title order={1} align="center" style={{ color: "#2c3e50" }}>
              <FormattedMessage id="app.signupForm.title" />
            </Title>
          </Box>
          
          <Paper p="md" shadow="xs" style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107" }}>
            <Stack spacing="xs">
              <Text weight={500} size="sm" color="dark">
                <FormattedMessage id="app.signupForm.requirements.title" defaultMessage="Required Fields to Submit:" />
              </Text>
              <Text size="sm" color="dimmed">
                ✓ <strong>Username, Email, Preferred Language</strong>
              </Text>
              <Text size="sm" color="dimmed">
                ✓ <strong>Profile Title, Subtitle, Description</strong>
              </Text>
              <Text size="sm" color="dimmed">
                ✓ <strong>Contact Name, Phone, Address</strong>
              </Text>
              <Text size="sm" color="dimmed">
                ✓ <strong>Main Crop</strong>
              </Text>
              <Text size="xs" color="dimmed" mt="xs" style={{ fontStyle: 'italic' }}>
                (All other fields are optional)
              </Text>
            </Stack>
          </Paper>

          <Accordion variant="separated" transitionDuration={0}>
            {/* Section 1: User Account & Profile Information */}
            <Accordion.Item value="userInfo">
              <Accordion.Control>
                <Title order={3}><FormattedMessage id="app.signupForm.section.userInfo" /></Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack spacing="xs" p={4}>

                  {/* ── Account ── */}
                  <Paper withBorder radius="md" p="sm" style={{ borderLeft: "4px solid #1c7ed6" }}>
                    <Stack spacing="md">
                      <Group spacing="xs">
                        <div style={{ width: 4, height: 20, background: "#1c7ed6", borderRadius: 2 }} />
                        <Text weight={700} size="md" color="#1c7ed6">
                          <FormattedMessage id="app.signupForm.accountDescription" defaultMessage="Account" />
                        </Text>
                      </Group>
                      <Grid>
                        <Grid.Col xs={12} md={6}>
                          <TextInput
                            label={<FormattedMessage id="app.signupForm.userName" />}
                            placeholder={intl.formatMessage({ id: "app.signupForm.userName.placeholder" })}
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            required
                            withAsterisk
                          />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput
                            label={<FormattedMessage id="app.signupForm.email" />}
                            placeholder={intl.formatMessage({ id: "app.signupForm.email.placeholder" })}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            type="email"
                            required
                            withAsterisk
                          />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <Select
                            label={<FormattedMessage id="app.signupForm.preferredLanguage" />}
                            placeholder={intl.formatMessage({ id: "app.signupForm.preferredLanguage.placeholder" })}
                            data={languageOptions}
                            value={preferredLanguageId}
                            onChange={setPreferredLanguageId}
                            required
                            withAsterisk
                            searchable
                            nothingFound="No languages found"
                            error={!languagesLoading && languageOptions.length === 0 ? "Failed to load languages" : null}
                          />
                        </Grid.Col>
                      </Grid>
                    </Stack>
                  </Paper>

                  {/* ── Profile ── */}
                  <Paper withBorder radius="md" p="sm" style={{ borderLeft: "4px solid #2f9e44" }}>
                    <Stack spacing="md">
                      <Group spacing="xs">
                        <div style={{ width: 4, height: 20, background: "#2f9e44", borderRadius: 2 }} />
                        <Text weight={700} size="md" color="#2f9e44">
                          <FormattedMessage id="app.signupForm.profileInfo" defaultMessage="Profile" />
                        </Text>
                      </Group>
                      <Text color="dimmed" size="sm">
                        <FormattedMessage id="app.signupForm.profileDescription" />
                      </Text>
                      <TextInput
                        label={<FormattedMessage id="app.signupForm.profileTitle" />}
                        value={profileTitle}
                        onChange={(e) => setProfileTitle(e.target.value)}
                        required
                        withAsterisk
                      />
                      <Textarea
                        label={<FormattedMessage id="app.signupForm.profileSubtitle" />}
                        value={profileSubtitle}
                        onChange={(e) => setProfileSubtitle(e.target.value)}
                        minRows={3}
                        required
                        withAsterisk
                      />
                      <Textarea
                        label={<FormattedMessage id="app.signupForm.description" />}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        minRows={4}
                        required
                        withAsterisk
                      />
                    </Stack>
                  </Paper>

                  {/* ── Contact ── */}
                  <Paper withBorder radius="md" p="sm" style={{ borderLeft: "4px solid #e67700" }}>
                    <Stack spacing="md">
                      <Group spacing="xs">
                        <div style={{ width: 4, height: 20, background: "#e67700", borderRadius: 2 }} />
                        <Text weight={700} size="md" color="#e67700">
                          <FormattedMessage id="app.signupForm.contactInfo" defaultMessage="Contact" />
                        </Text>
                      </Group>
                      <Grid>
                        <Grid.Col xs={12} md={6}>
                          <TextInput
                            label={<FormattedMessage id="app.signupForm.contactName" />}
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            required
                            withAsterisk
                          />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput
                            label={<FormattedMessage id="app.signupForm.contactPhone" />}
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            required
                            withAsterisk
                          />
                        </Grid.Col>
                        <Grid.Col xs={12}>
                          <Textarea
                            label={<FormattedMessage id="app.signupForm.contactAddress" />}
                            value={contactAddress}
                            onChange={(e) => setContactAddress(e.target.value)}
                            minRows={2}
                            required
                            withAsterisk
                          />
                        </Grid.Col>
                      </Grid>
                    </Stack>
                  </Paper>

                  {/* ── Farm Info Grid ── */}
                  <Paper withBorder radius="md" p="sm" style={{ borderLeft: "4px solid #087f5b" }}>
                    <Stack spacing="md">
                      <Group spacing="xs">
                        <div style={{ width: 4, height: 20, background: "#087f5b", borderRadius: 2 }} />
                        <Text weight={700} size="md" color="#087f5b">
                          <FormattedMessage id="app.signupForm.infoGrid" defaultMessage="Farm Info" />
                        </Text>
                      </Group>
                      <Text size="sm" color="dimmed">
                        <FormattedMessage id="app.signupForm.infoGrid.description" defaultMessage="Fill in your farm information below. Only Main Crop is required, other fields are optional." />
                      </Text>
                      <Grid gutter="md">
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.producer" />} value={producer} onChange={(e) => setProducer(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.locationInfo" />} value={locationInfo} onChange={(e) => setLocationInfo(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.extension" />} value={extension} onChange={(e) => setExtension(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.mainCrop" />} value={mainCrop} onChange={(e) => setMainCrop(e.target.value)} required withAsterisk />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.experience" />} value={experience} onChange={(e) => setExperience(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.practices" />} value={practices} onChange={(e) => setPractices(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.certifications" />} value={certifications} onChange={(e) => setCertifications(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                          <TextInput label={<FormattedMessage id="app.signupForm.altitude" />} value={altitude} onChange={(e) => setAltitude(e.target.value)} />
                        </Grid.Col>
                      </Grid>
                    </Stack>
                  </Paper>

                  {/* ── Social Media ── */}
                  <Paper withBorder radius="md" p="sm" style={{ borderLeft: "4px solid #862e9c" }}>
                    <Stack spacing="md">
                      <Group spacing="xs">
                        <div style={{ width: 4, height: 20, background: "#862e9c", borderRadius: 2 }} />
                        <Text weight={700} size="md" color="#862e9c">
                          <FormattedMessage id="app.signupForm.socialMedia" defaultMessage="Social Media" />
                        </Text>
                      </Group>
                      <Grid>
                        <Grid.Col xs={12} md={4}>
                          <TextInput label={<FormattedMessage id="app.signupForm.instagram" />} placeholder="@username" value={instagram} onChange={(e) => setInstagram(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={4}>
                          <TextInput label={<FormattedMessage id="app.signupForm.twitter" />} placeholder="@username" value={twitter} onChange={(e) => setTwitter(e.target.value)} />
                        </Grid.Col>
                        <Grid.Col xs={12} md={4}>
                          <TextInput label={<FormattedMessage id="app.signupForm.website" />} placeholder="https://example.com" value={website} onChange={(e) => setWebsite(e.target.value)} />
                        </Grid.Col>
                      </Grid>
                    </Stack>
                  </Paper>

                  {/* ── Location ── */}
                  <Paper withBorder radius="md" p="sm" style={{ borderLeft: "4px solid #1098ad" }}>
                    <Stack spacing="md">
                      <Group spacing="xs">
                        <div style={{ width: 4, height: 20, background: "#1098ad", borderRadius: 2 }} />
                        <Text weight={700} size="md" color="#1098ad">
                          <FormattedMessage id="app.signupForm.location" defaultMessage="Location" />
                        </Text>
                      </Group>
                      <Textarea
                        label={<FormattedMessage id="app.signupForm.locationDescription" />}
                        value={locationDescription}
                        onChange={(e) => setLocationDescription(e.target.value)}
                        minRows={2}
                      />
                    </Stack>
                  </Paper>

                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Section 2: Farm Fields */}
            <Accordion.Item value="farmFields">
              <Accordion.Control>
                <Group position="apart" style={{ width: '100%', paddingRight: '20px' }}>
                  <Title order={3}><FormattedMessage id="app.signupForm.section.farmFields" /></Title>
                  <Badge size="lg" variant="filled" color="blue">{farmFields.length}</Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Box p="md">
                  <Stack spacing="md">
                  {farmFields.map((field, fieldIndex) => (
                    <Box key={fieldIndex}>
                      <Accordion
                        variant="separated"
                        styles={{
                          item: { border: '2px solid #e9ecef', borderRadius: '8px' },
                          control: { '&:hover': { backgroundColor: '#f8f9fa' } }
                        }}
                      >
                        <Accordion.Item value={`field-${fieldIndex}`}>
                          <Accordion.Control>
                            <Group position="apart" style={{ width: '100%' }}>
                              <Group>
                                <Badge size="lg" color="teal" variant="light">{fieldIndex + 1}</Badge>
                                <div>
                                  <Text weight={500} size="md">
                                    {field.name || <Text color="dimmed" component="span"><FormattedMessage id="app.signupForm.unnamedField" defaultMessage="Unnamed Field" /></Text>}
                                  </Text>
                                  <Text size="sm" color="dimmed">
                                    {field.product || intl.formatMessage({ id: "app.signupForm.noProduct", defaultMessage: "No product" })} • {field.coordinates.length} GPS point{field.coordinates.length !== 1 ? 's' : ''}
                                  </Text>
                                </div>
                              </Group>
                              {farmFields.length > 1 && (
                                <ActionIcon 
                                  color="red" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFarmField(fieldIndex);
                                  }}
                                  style={{ marginRight: '10px' }}
                                >
                                  <Trash2 size={18} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack spacing="md">
                        <TextInput
                          label={<FormattedMessage id="app.signupForm.fieldName" />}
                          value={field.name}
                          onChange={(e) => {
                            const updated = [...farmFields];
                            updated[fieldIndex].name = e.target.value;
                            setFarmFields(updated);
                          }}
                          required
                        />
                        <TextInput
                          label={<FormattedMessage id="app.signupForm.fieldProduct" />}
                          value={field.product}
                          onChange={(e) => {
                            const updated = [...farmFields];
                            updated[fieldIndex].product = e.target.value;
                            setFarmFields(updated);
                          }}
                        />
                        <TextInput
                          label={<FormattedMessage id="app.signupForm.fieldRegion" />}
                          value={field.region}
                          onChange={(e) => {
                            const updated = [...farmFields];
                            updated[fieldIndex].region = e.target.value;
                            setFarmFields(updated);
                          }}
                        />
                        
                        <Title order={6}><FormattedMessage id="app.signupForm.gpsCoordinates" /></Title>
                        {field.coordinates.map((coord, coordIndex) => (
                          <Box key={coordIndex}>
                            <Grid gutter="xs" align="center">
                              <Grid.Col span={4}>
                                <TextInput
                                  placeholder={intl.formatMessage({ id: "app.signupForm.latitude" })}
                                  value={coord.lat}
                                  onChange={(e) => {
                                    const updated = [...farmFields];
                                    updated[fieldIndex].coordinates[coordIndex].lat = e.target.value;
                                    setFarmFields(updated);
                                  }}
                                  required
                                />
                              </Grid.Col>
                              <Grid.Col span={4}>
                                <TextInput
                                  placeholder={intl.formatMessage({ id: "app.signupForm.longitude" })}
                                  value={coord.lng}
                                  onChange={(e) => {
                                    const updated = [...farmFields];
                                    updated[fieldIndex].coordinates[coordIndex].lng = e.target.value;
                                    setFarmFields(updated);
                                  }}
                                  required
                                />
                              </Grid.Col>
                              <Grid.Col span={3}>
                                <Button
                                  variant="light"
                                  size="xs"
                                  leftIcon={<IconMapPin size={16} />}
                                  onClick={() => {
                                    // Reset map to user's location when opening
                                    if (userLocation) {
                                      setMapView({ center: userLocation, zoom: 13 });
                                    }
                                    setSelectingCoordIndex({ fieldIndex, coordIndex });
                                    setMapModalOpen(true);
                                  }}
                                  fullWidth
                                >
                                  <FormattedMessage id="app.signupForm.selectFromMap" />
                                </Button>
                              </Grid.Col>
                              <Grid.Col span={1}>
                                {field.coordinates.length > 1 && (
                                  <ActionIcon color="red" onClick={() => removeCoordinate(fieldIndex, coordIndex)}>
                                    <Trash2 size={18} />
                                  </ActionIcon>
                                )}
                              </Grid.Col>
                            </Grid>
                          </Box>
                        ))}
                        <Button
                          variant="light"
                          leftIcon={<Plus size={18} />}
                          onClick={() => addCoordinate(fieldIndex)}
                        >
                          <FormattedMessage id="app.signupForm.addCoordinate" />
                        </Button>
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                    </Box>
                  ))}
                  
                  <Button
                    leftIcon={<Plus size={18} />}
                    onClick={addFarmField}
                    variant="outline"
                    fullWidth
                    size="md"
                  >
                    <FormattedMessage id="app.signupForm.addFarmField" />
                  </Button>
                </Stack>
                </Box>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Section 3: Product Lots */}
            <Accordion.Item value="productLots">
              <Accordion.Control>
                <Group position="apart" style={{ width: '100%', paddingRight: '20px' }}>
                  <Title order={3}><FormattedMessage id="app.signupForm.section.productLots" /></Title>
                  <Badge size="lg" variant="filled" color="green">{productLots.length}</Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Box p="md">
                  <Stack spacing="md">
                  {productLots.map((lot, lotIndex) => (
                    <Box key={lotIndex}>
                      <Accordion
                        variant="separated"
                        styles={{
                          item: { border: '2px solid #e9ecef', borderRadius: '8px' },
                          control: { '&:hover': { backgroundColor: '#f8f9fa' } }
                        }}
                      >
                        <Accordion.Item value={`lot-${lotIndex}`}>
                          <Accordion.Control>
                            <Group position="apart" style={{ width: '100%' }}>
                              <Group>
                                <Badge size="lg" color="grape" variant="light">{lotIndex + 1}</Badge>
                                <div>
                                  <Text weight={500} size="md">
                                    {lot.ProductInputValue || <Text color="dimmed" component="span"><FormattedMessage id="app.signupForm.noProductSelected" defaultMessage="No product selected" /></Text>}
                                  </Text>
                                  <Text size="sm" color="dimmed">
                                    {lot.Quantity ? `${lot.Quantity} ${lot.UOMValue || ''}` : intl.formatMessage({ id: "app.signupForm.noQuantity", defaultMessage: "No quantity" })} • 
                                    {lot.transportations.length} transport{lot.transportations.length !== 1 ? 's' : ''} • 
                                    {lot.transformations.length} transformation{lot.transformations.length !== 1 ? 's' : ''}
                                  </Text>
                                </div>
                              </Group>
                              {productLots.length > 1 && (
                                <ActionIcon 
                                  color="red" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeProductLot(lotIndex);
                                  }}
                                  style={{ marginRight: '10px' }}
                                >
                                  <Trash2 size={18} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack spacing="md">
                        <Autocomplete
                          label={<FormattedMessage id="app.signupForm.productCategory" />}
                          placeholder={intl.formatMessage({ id: 'app.signupForm.productCategory.placeholder', defaultMessage: 'e.g., Coffee, Cocoa, Almond, Tea, Rice' })}
                          data={Object.values(smallestProductIds).map(({ label }) => label)}
                          value={lot.ProductInputValue}
                          onChange={(value) => {
                            const updated = [...productLots];
                            updated[lotIndex].ProductInputValue = value;
                            
                            // Find the selected product by label
                            const selectedOption = Object.values(smallestProductIds).find(
                              option => option.label.toLowerCase() === value.toLowerCase()
                            );
                            if (selectedOption) {
                              updated[lotIndex].ProductId = parseInt(selectedOption.id);
                            }
                            setProductLots(updated);
                          }}
                          required
                          styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
                        />
                        <NumberInput
                          label={<FormattedMessage id="app.signupForm.quantity" />}
                          value={lot.Quantity}
                          onChange={(val) => {
                            const updated = [...productLots];
                            updated[lotIndex].Quantity = val;
                            setProductLots(updated);
                          }}
                          required
                        />
                        <Select
                          label={<FormattedMessage id="app.signupForm.unit" />}
                          placeholder={intl.formatMessage({ id: 'app.signupForm.unit.placeholder', defaultMessage: 'Select unit' })}
                          data={unitOptions}
                          value={lot.UOMValue}
                          onChange={(value) => {
                            const updated = [...productLots];
                            updated[lotIndex].UOMValue = value;
                            // Find and store UOMId
                            const selectedUOM = UOMList.find(uom => uom.UOM === value);
                            if (selectedUOM) {
                              updated[lotIndex].UOMId = selectedUOM.UOMId;
                            }
                            setProductLots(updated);
                          }}
                          searchable
                          required
                          styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
                        />
                        <Autocomplete
                          label={<FormattedMessage id="app.signupForm.region" />}
                          placeholder={intl.formatMessage({ id: 'app.signupForm.region.placeholder', defaultMessage: 'e.g., Minas Gerais, Brazil' })}
                          data={locationSuggestions.map(loc => ({ value: loc.description, id: loc.place_id }))}
                          value={lot.RegionQuery || ""}
                          onChange={(value) => {
                            const updated = [...productLots];
                            updated[lotIndex].RegionQuery = value;
                            const selected = locationSuggestions.find(loc => loc.description === value);
                            if (selected) {
                              updated[lotIndex].selectedLocation = selected;
                              updated[lotIndex].RegionId = selected.place_id;
                            }
                            setProductLots(updated);
                            searchLocations(value);
                          }}
                          required
                          styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
                        />
                        <Grid>
                          <Grid.Col span={6}>
                            <TextInput
                              type="date"
                              label={<FormattedMessage id="app.signupForm.periodStart" defaultMessage="Period Start" />}
                              value={lot.periodStart}
                              onChange={(e) => {
                                const updated = [...productLots];
                                updated[lotIndex].periodStart = e.target.value;
                                // Combine start and end into Period field
                                if (updated[lotIndex].periodEnd) {
                                  updated[lotIndex].Period = `${e.target.value} - ${updated[lotIndex].periodEnd}`;
                                }
                                setProductLots(updated);
                              }}
                              required
                            />
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <TextInput
                              type="date"
                              label={<FormattedMessage id="app.signupForm.periodEnd" defaultMessage="Period End" />}
                              value={lot.periodEnd}
                              onChange={(e) => {
                                const updated = [...productLots];
                                updated[lotIndex].periodEnd = e.target.value;
                                // Combine start and end into Period field
                                if (updated[lotIndex].periodStart) {
                                  updated[lotIndex].Period = `${updated[lotIndex].periodStart} - ${e.target.value}`;
                                }
                                setProductLots(updated);
                              }}
                              required
                            />
                          </Grid.Col>
                        </Grid>
                        <Textarea
                          label={<FormattedMessage id="app.signupForm.productLotDetail" />}
                          value={lot.ProductLotDetail}
                          onChange={(e) => {
                            const updated = [...productLots];
                            updated[lotIndex].ProductLotDetail = e.target.value;
                            setProductLots(updated);
                          }}
                          minRows={2}
                        />

                        {/* Transportation Events */}
                        <Title order={6} mt="md"><FormattedMessage id="app.signupForm.transportations" /></Title>
                        {lot.transportations.map((trans, transIndex) => (
                          <Paper key={transIndex} p="sm" withBorder style={{ backgroundColor: "#f9fafb" }}>
                            <Stack spacing="xs">
                              <Grid align="center">
                                <Grid.Col span={10}>
                                  <Text size="sm" weight={500}><FormattedMessage id="app.signupForm.transportation" /> {transIndex + 1}</Text>
                                </Grid.Col>
                                <Grid.Col span={2} style={{ textAlign: "right" }}>
                                  <ActionIcon size="sm" color="red" onClick={() => removeTransportation(lotIndex, transIndex)}>
                                    <Trash2 size={16} />
                                  </ActionIcon>
                                </Grid.Col>
                              </Grid>
                              <Autocomplete
                                label={intl.formatMessage({ id: "app.signupForm.location" })}
                                placeholder={intl.formatMessage({ id: "app.signupForm.location.placeholder", defaultMessage: "e.g., Guatemala City, Guatemala" })}
                                data={locationSuggestions.map(loc => ({ value: loc.description, id: loc.place_id }))}
                                value={trans.locationQuery || ""}
                                onChange={(value) => {
                                  const updated = [...productLots];
                                  updated[lotIndex].transportations[transIndex].locationQuery = value;
                                  const selected = locationSuggestions.find(loc => loc.description === value);
                                  if (selected) {
                                    updated[lotIndex].transportations[transIndex].selectedLocation = selected;
                                    updated[lotIndex].transportations[transIndex].location_id = selected.place_id;
                                  }
                                  setProductLots(updated);
                                  searchLocations(value);
                                }}
                                size="sm"
                                styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
                              />
                              <Grid>
                                <Grid.Col span={6}>
                                  <TextInput
                                    type="date"
                                    label={intl.formatMessage({ id: "app.signupForm.periodStart", defaultMessage: "Period Start" })}
                                    value={trans.periodStart}
                                    onChange={(e) => {
                                      const updated = [...productLots];
                                      updated[lotIndex].transportations[transIndex].periodStart = e.target.value;
                                      setProductLots(updated);
                                    }}
                                    size="sm"
                                  />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                  <TextInput
                                    type="date"
                                    label={intl.formatMessage({ id: "app.signupForm.periodEnd", defaultMessage: "Period End" })}
                                    value={trans.periodEnd}
                                    onChange={(e) => {
                                      const updated = [...productLots];
                                      updated[lotIndex].transportations[transIndex].periodEnd = e.target.value;
                                      setProductLots(updated);
                                    }}
                                    size="sm"
                                  />
                                </Grid.Col>
                              </Grid>
                              <Textarea
                                label={intl.formatMessage({ id: "app.signupForm.description" })}
                                placeholder={intl.formatMessage({ id: "app.signupForm.description.placeholder", defaultMessage: "Describe the transportation details" })}
                                value={trans.transportDescription}
                                onChange={(e) => {
                                  const updated = [...productLots];
                                  updated[lotIndex].transportations[transIndex].transportDescription = e.target.value;
                                  setProductLots(updated);
                                }}
                                minRows={2}
                                size="sm"
                              />
                            </Stack>
                          </Paper>
                        ))}
                        <Button
                          variant="light"
                          size="sm"
                          leftIcon={<Plus size={16} />}
                          onClick={() => addTransportation(lotIndex)}
                        >
                          <FormattedMessage id="app.signupForm.addTransportation" />
                        </Button>

                        {/* Transformation Events */}
                        <Title order={6} mt="md"><FormattedMessage id="app.signupForm.transformations" /></Title>
                        {lot.transformations.map((trans, transIndex) => (
                          <Paper key={transIndex} p="sm" withBorder style={{ backgroundColor: "#f9fafb" }}>
                            <Stack spacing="xs">
                              <Grid align="center">
                                <Grid.Col span={10}>
                                  <Text size="sm" weight={500}><FormattedMessage id="app.signupForm.transformation" /> {transIndex + 1}</Text>
                                </Grid.Col>
                                <Grid.Col span={2} style={{ textAlign: "right" }}>
                                  <ActionIcon size="sm" color="red" onClick={() => removeTransformation(lotIndex, transIndex)}>
                                    <Trash2 size={16} />
                                  </ActionIcon>
                                </Grid.Col>
                              </Grid>
                              <TextInput
                                label={intl.formatMessage({ id: "app.signupForm.physicalState" })}
                                placeholder={intl.formatMessage({ id: "app.signupForm.physicalState.placeholder", defaultMessage: "e.g., Roasted, Fermented, Dried" })}
                                value={trans.physicalState}
                                onChange={(e) => {
                                  const updated = [...productLots];
                                  updated[lotIndex].transformations[transIndex].physicalState = e.target.value;
                                  setProductLots(updated);
                                }}
                                size="sm"
                              />
                              <Autocomplete
                                label={intl.formatMessage({ id: "app.signupForm.location" })}
                                placeholder={intl.formatMessage({ id: "app.signupForm.location.placeholder", defaultMessage: "Select transformation location" })}
                                data={locationSuggestions.map(loc => loc.description)}
                                value={trans.locationQuery}
                                onChange={(value) => {
                                  const updated = [...productLots];
                                  updated[lotIndex].transformations[transIndex].locationQuery = value;
                                  const selected = locationSuggestions.find(loc => loc.description === value);
                                  if (selected) {
                                    updated[lotIndex].transformations[transIndex].selectedLocation = selected;
                                    updated[lotIndex].transformations[transIndex].location_id = selected.place_id;
                                  }
                                  setProductLots(updated);
                                  searchLocations(value);
                                }}
                                size="sm"
                                styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
                              />
                              <Grid>
                                <Grid.Col span={6}>
                                  <TextInput
                                    type="date"
                                    label={intl.formatMessage({ id: "app.signupForm.periodStart", defaultMessage: "Period Start" })}
                                    value={trans.periodStart}
                                    onChange={(e) => {
                                      const updated = [...productLots];
                                      updated[lotIndex].transformations[transIndex].periodStart = e.target.value;
                                      setProductLots(updated);
                                    }}
                                    size="sm"
                                  />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                  <TextInput
                                    type="date"
                                    label={intl.formatMessage({ id: "app.signupForm.periodEnd", defaultMessage: "Period End" })}
                                    value={trans.periodEnd}
                                    onChange={(e) => {
                                      const updated = [...productLots];
                                      updated[lotIndex].transformations[transIndex].periodEnd = e.target.value;
                                      setProductLots(updated);
                                    }}
                                    size="sm"
                                  />
                                </Grid.Col>
                              </Grid>
                              <Textarea
                                label={intl.formatMessage({ id: "app.signupForm.description" })}
                                placeholder={intl.formatMessage({ id: "app.signupForm.description.placeholder", defaultMessage: "Describe the transformation process" })}
                                value={trans.transportDescription}
                                onChange={(e) => {
                                  const updated = [...productLots];
                                  updated[lotIndex].transformations[transIndex].transportDescription = e.target.value;
                                  setProductLots(updated);
                                }}
                                minRows={2}
                                size="sm"
                              />
                            </Stack>
                          </Paper>
                        ))}
                        <Button
                          variant="light"
                          size="sm"
                          leftIcon={<Plus size={16} />}
                          onClick={() => addTransformation(lotIndex)}
                        >
                          <FormattedMessage id="app.signupForm.addTransformation" />
                        </Button>
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                    </Box>
                  ))}
                  
                  <Button
                    leftIcon={<Plus size={18} />}
                    onClick={addProductLot}
                    variant="outline"
                    fullWidth
                    size="md"
                  >
                    <FormattedMessage id="app.signupForm.addProductLot" />
                  </Button>
                </Stack>
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* Fees */}
          <Paper withBorder radius="md" p="lg" shadow="xs">
            <Title order={3}><FormattedMessage id="app.signupForm.fees.title" /></Title>
            <Text size="sm" color="dimmed" mb="sm">
              <FormattedMessage id="app.signupForm.fees.intro" />
            </Text>
            <Table striped verticalSpacing="xs">
              <thead>
                <tr>
                  <th><FormattedMessage id="app.signupForm.fees.colService" /></th>
                  <th><FormattedMessage id="app.signupForm.fees.colUnit" /></th>
                  <th style={{ textAlign: "right" }}><FormattedMessage id="app.signupForm.fees.colPrice" /></th>
                </tr>
              </thead>
              <tbody>
                {SIGNUP_FEES.map((fee) => (
                  <tr key={fee.id}>
                    <td><FormattedMessage id={`app.signupForm.fees.${fee.id}`} /></td>
                    <td><FormattedMessage id={`app.signupForm.fees.${fee.id}.unit`} /></td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                      {fee.price !== null
                        ? intl.formatNumber(fee.price, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
                        : <FormattedMessage id="app.signupForm.fees.onQuote" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Paper>

          {/* Success Message */}
          {submitSuccess && (
            <Paper p="lg" shadow="sm" style={{ backgroundColor: "#d4edda", border: "1px solid #c3e6cb" }}>
              <Stack spacing="sm" align="center">
                <IconCheck size={48} color="#28a745" />
                <Title order={3} style={{ color: "#155724" }}>
                  <FormattedMessage id="app.signupForm.success.title" defaultMessage="Form Submitted Successfully!" />
                </Title>
                <Text size="md" style={{ color: "#155724" }} align="center">
                  <FormattedMessage 
                    id="app.signupForm.success.message" 
                    defaultMessage="Thank you for your registration. Verto will review your information and contact you once your account is validated." 
                  />
                </Text>
              </Stack>
            </Paper>
          )}

          {/* Error Message */}
          {submitError && (
            <Paper p="md" shadow="sm" style={{ backgroundColor: "#f8d7da", border: "1px solid #f5c6cb" }}>
              <Stack spacing="xs" align="center">
                <IconX size={32} color="#dc3545" />
                <Text size="sm" style={{ color: "#721c24" }} align="center">
                  {submitError}
                </Text>
              </Stack>
            </Paper>
          )}

          {/* Submit Button */}
          {!submitSuccess && (
            <>
              <Button
                size="lg"
                color="green"
                onClick={handleSubmit}
                disabled={!isFormValid() || submitting}
                loading={submitting}
                style={{ marginTop: "2rem" }}
              >
                <FormattedMessage id="app.signupForm.submit" />
              </Button>
              
              {!isFormValid() && !submitting && (
                <Paper p="md" mt="md" style={{ backgroundColor: "#ffe0e0", border: "1px solid #ff4444" }}>
                  <Text size="sm" color="red" weight={600} mb="xs">
                    <FormattedMessage id="app.signupForm.requirements.incomplete" defaultMessage="Missing Required Fields:" />
                  </Text>
                  <Stack spacing="xs">
                    {getMissingFields().map((field, idx) => (
                      <Text key={idx} size="sm" color="red">
                        • {field}
                      </Text>
                    ))}
                  </Stack>
                </Paper>
              )}
            </>
          )}
        </Stack>
      </Container>
      
      {/* Map Selection Modal */}
      <Modal
        opened={mapModalOpen}
        onClose={() => {
          setMapModalOpen(false);
          setSelectingCoordIndex(null);
        }}
        size="xl"
        title={<Title order={4}><FormattedMessage id="app.signupForm.selectCoordinateFromMap" /></Title>}
      >
        <Text color="dimmed" size="sm" mb="md">
          <FormattedMessage id="app.signupForm.mapInstructions" />
        </Text>
        <Box style={{ height: '500px', position: 'relative' }}>
          <InteractiveMap
            fieldData={[]}
            coordinateSelectionMode={true}
            initialCenter={mapView.center}
            initialZoom={mapView.zoom}
            onViewChange={setMapView}
            onCoordinateSelect={(lat, lng) => {
              if (selectingCoordIndex) {
                const { fieldIndex, coordIndex } = selectingCoordIndex;
                const updated = [...farmFields];
                updated[fieldIndex].coordinates[coordIndex] = {
                  lat: lat.toFixed(6),
                  lng: lng.toFixed(6)
                };
                setFarmFields(updated);
                setMapModalOpen(false);
                setSelectingCoordIndex(null);
              }
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default SignUpFormPage;
